// src/pages/ServicesPage.tsx
import React from "react";
import { useNavigate } from "react-router-dom";
import CheckIcon from "../components/common/CheckIcon";
import ServiceCard from "../components/common/ServiceCard";
import wealthMethodBook from "../assets/images/mockup2.png";
import dbcImg from "../assets/images/Blurred shirt.png";
import faithImg from "../assets/images/adeolu.png";
import techImg from "../assets/images/tech.png";

const ServicesPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white text-brandNavy">
      <header className="relative overflow-hidden bg-brandNavy text-white">
        {/* subtle dotted pattern */}
        <div className="absolute inset-0 pointer-events-none opacity-[0.15]">
          <svg
            className="w-full h-full"
            preserveAspectRatio="none"
            viewBox="0 0 1200 400"
          >
            <defs>
              <pattern
                id="dotsPattern"
                width="22"
                height="22"
                patternUnits="userSpaceOnUse"
              >
                <circle cx="2" cy="2" r="2" fill="#ffffff" opacity="0.25" />
              </pattern>
            </defs>

            <rect width="1200" height="400" fill="url(#dotsPattern)" />
          </svg>
        </div>

        {/* HERO CONTENT */}
        <div className="relative max-w-5xl mx-auto px-6 py-8 flex flex-col items-center text-center">
          {/* HEADLINE */}
          <h1 className="max-w-4xl leading-tight">
            <span className="block text-4xl md:text-5xl lg:text-6xl font-extrabold text-white">
              Turn Your Skills Into Income — Starting Now
            </span>

            <span className="block text-3xl md:text-4xl lg:text-5xl font-extrabold text-brandRed/70 mt-2">
              Discover What People Will Pay You For
            </span>
          </h1>

          {/* SUBTEXT */}
          <p className="max-w-2xl text-base md:text-lg text-white/80 mt-5">
            The D.B.C. Formula helps busy Nigerian professionals identify their
            most profitable skill, position themselves as trusted authorities,
            and convert their knowledge into consistent income — without leaving
            their 9–5. Gain clarity, structure, and a proven path for monetizing
            what you already know.
          </p>

          {/* CTA BUTTON */}
          <div className="mt-8">
            <button
              onClick={() =>
                window.open(
                  "/skill-to-sales/quiz",
                  "_blank",
                  "noopener,noreferrer"
                )
              }
              className="inline-flex items-center justify-center bg-brandRed hover:bg-brandRedHover text-white font-semibold px-8 py-4 rounded-xl shadow-xl transition-transform transform hover:-translate-y-1 text-lg"
            >
              See How Much Money Your Knowledge Is Worth
            </button>
          </div>
        </div>

        {/* bottom curve */}
        <div className="absolute left-0 right-0 bottom-0 pointer-events-none">
          <svg
            className="w-full h-10 fill-white"
            viewBox="0 0 1200 120"
            preserveAspectRatio="none"
          >
            <path d="M0,0 C300,80 600,80 900,40 L1200,20 L1200,120 L0,120 Z"></path>
          </svg>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-16">
        {/* Wealth Method Book Section */}
        <section className="mb-20">
          <h2 className="text-2xl font-bold text-brandNavy mb-6">
            The Wealth Method — Book & Framework
          </h2>

          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div className="w-full">
              <div className="rounded-2xl overflow-hidden shadow-lg">
                <img
                  src={wealthMethodBook}
                  alt="The Wealth Method — Book"
                  className="object-cover w-full h-full"
                />
              </div>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-brandNavy mb-4">
                The Wealth Method
              </h3>
              <p className="text-mutedGray mb-6">
                A practical, spiritual, and actionable blueprint for building
                wealth with clarity, assignment, and systems. Learn the
                principles, patterns, and prophetic intelligence behind wealth
                creation in any economy.
              </p>

              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => navigate("/wealth-method")}
                  className="bg-transparent border border-lightBorder text-brandNavy px-5 py-3 rounded font-semibold hover:bg-brandNavy/5 transition"
                >
                  Learn more
                </button>

                <button
                  onClick={() => navigate("/checkout?product=wealth-method")}
                  className="bg-brandRed text-brandWhite px-5 py-3 rounded font-semibold hover:bg-brandRedHover transition"
                >
                  Buy now
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Services */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-brandNavy">How I help</h2>
          <p className="mt-3 text-mutedGray">
            My offerings are a mix of training, done-with-you implementations,
            and high-touch advisory. Choose the path that fits your season and
            capacity.
          </p>
        </section>

        {/* Services grid */}
        <section className="grid md:grid-cols-3 gap-6 mb-16">
          <ServiceCard
            title="Funnel & Digital Accelerator"
            description="Build production-ready funnels (MERN + RTK) with deployment support. Includes codebase, walkthroughs and deployment assistance."
            image={dbcImg}
            link="/accelerator"
            buttonText="See Curriculum"
            variant="top"
          />

          <ServiceCard
            title="Masterclass & Challenges"
            description="Short, high-impact cohorts (3-day prophetic challenges) that pair training with email nurture and live Q&A invites."
            image={faithImg}
            link="/#free-masterclass"
            buttonText="Join Next Cohort"
            variant="top"
          />

          <ServiceCard
            title="Advisory & Strategy"
            description="High-touch strategy for leaders: gate mapping, monetization blueprints, and accountability roadmaps to steward influence well."
            image={techImg}
            link="/contact"
            buttonText="Book A Consult"
            variant="top"
          />
        </section>

        {/* Benefits / Why */}
        <section className="mb-12 grid md:grid-cols-2 gap-8 items-start">
          <div>
            <h3 className="text-xl font-semibold text-brandNavy">
              Why this works
            </h3>
            <p className="mt-3 text-mutedGray">
              We blend revelation and repeatable systems. The result is
              spiritual clarity married to technical execution — so your
              influence turns into sustainable impact and income.
            </p>

            <ul className="mt-4 space-y-3">
              <li className="flex items-start gap-3 text-mutedGray">
                <CheckIcon className="text-brandRed mt-1" />
                <div>
                  <strong>Proven funnels:</strong> Launch funnels that actually
                  convert.
                </div>
              </li>
              <li className="flex items-start gap-3 text-mutedGray">
                <CheckIcon className="text-brandRed mt-1" />
                <div>
                  <strong>Systems-first:</strong> Build repeatable & scalable
                  processes.
                </div>
              </li>
              <li className="flex items-start gap-3 text-mutedGray">
                <CheckIcon className="text-brandRed mt-1" />
                <div>
                  <strong>Kingdom focus:</strong> Spiritual discernment guides
                  every strategy.
                </div>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="text-xl font-semibold text-brandNavy">
              Who it's for
            </h3>
            <p className="mt-3 text-mutedGray">
              Pastors, ministry leaders, founders, coaches and creators who want
              to monetize their calling while stewarding influence well.
            </p>

            <div className="mt-4 space-y-3">
              <div className="flex items-center gap-3 text-mutedGray">
                <CheckIcon className="text-brandRed" />
                <span>Leaders launching paid offerings</span>
              </div>
              <div className="flex items-center gap-3 text-mutedGray">
                <CheckIcon className="text-brandRed" />
                <span>Ministries scaling outreach & donations</span>
              </div>
              <div className="flex items-center gap-3 text-mutedGray">
                <CheckIcon className="text-brandRed" />
                <span>Creators building educational products</span>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="mb-12">
          <h3 className="text-xl font-semibold text-brandNavy">Testimonials</h3>
          <div className="mt-6 grid md:grid-cols-3 gap-6">
            <div className="p-4 bg-white rounded-lg border border-lightBorder">
              <div className="font-semibold text-brandNavy">A. Olu</div>
              <div className="text-sm mt-2 text-mutedGray">
                “This changed how I think about ministry and systems.”
              </div>
            </div>
            <div className="p-4 bg-white rounded-lg border border-lightBorder">
              <div className="font-semibold text-brandNavy">B. Chike</div>
              <div className="text-sm mt-2 text-mutedGray">
                “We launched our first paid funnel in 2 weeks with Adeolu’s
                codebase.”
              </div>
            </div>
            <div className="p-4 bg-white rounded-lg border border-lightBorder">
              <div className="font-semibold text-brandNavy">C. Grace</div>
              <div className="text-sm mt-2 text-mutedGray">
                “The prophetic challenge is powerful and practical.”
              </div>
            </div>
          </div>
        </section>

        {/* FAQ / Details */}
        <section className="mb-12">
          <h3 className="text-xl font-semibold text-brandNavy">
            Frequently asked
          </h3>
          <div className="mt-6 space-y-4">
            <details className="bg-white rounded p-4 border border-lightBorder">
              <summary className="font-semibold cursor-pointer">
                How do I get started?
              </summary>
              <div className="mt-2 text-mutedGray">
                Book a consult or join the masterclass to experience the
                training and decide the right next step.
              </div>
            </details>

            <details className="bg-white rounded p-4 border border-lightBorder">
              <summary className="font-semibold cursor-pointer">
                Do you offer done-for-you services?
              </summary>
              <div className="mt-2 text-mutedGray">
                Yes — for ministries and organisations we provide tailored
                funnels, deployment and staff training.
              </div>
            </details>
          </div>
        </section>

        {/* CTA */}
        <section className="text-center mb-12">
          <h3 className="text-2xl font-bold text-brandNavy">
            Ready to take the next step?
          </h3>
          <p className="mt-2 text-mutedGray">
            Choose the path — training, implementation or strategy.
          </p>
          <div className="mt-4 flex justify-center gap-4">
            <button
              onClick={() => navigate("/pricing")}
              className="bg-brandRed text-brandWhite px-6 py-3 rounded font-semibold hover:bg-brandRedHover transition"
            >
              See Pricing
            </button>
            <button
              onClick={() => navigate("/contact")}
              className="px-6 py-3 rounded border border-lightBorder text-brandNavy"
            >
              Contact
            </button>
          </div>
        </section>
      </main>

      {/* footer (keeps visual consistency with other pages) */}
      <footer className="border-t py-6">
        <div className="max-w-6xl mx-auto px-6 text-sm flex flex-col md:flex-row justify-between items-center">
          <div className="text-mutedGray">
            © {new Date().getFullYear()} Adeolu Adegbemi — The Blueprint
            Architect
          </div>
          <div className="mt-3 md:mt-0">
            <a className="mr-4 hover:underline text-brandNavy" href="/about">
              About
            </a>
            <a className="mr-4 hover:underline text-brandNavy" href="/contact">
              Contact
            </a>
            <a className="hover:underline text-brandNavy" href="/privacy">
              Privacy
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ServicesPage;
